/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, Type } from '@angular/core';

import { PopupAnchor } from './popup-anchor.directive';
import { PopupCoreService } from './popup-core.service';
import { Popup } from './Popup';
import { last } from 'rxjs/operators';
import { PopupOutput } from './interfaces';
import { SimplePopupComponent } from './simple-popup/simple-popup.component';

/** Jump to [[PopupCoreService]] for doc */

@Injectable()
export class PopupService {
	constructor(private popupCore: PopupCoreService) {}

	private _anchor: PopupAnchor;
	set anchor(anchor: PopupAnchor) {
		if (this._anchor) {
			throw new Error(
				'Only one anchor can be declared per PopupService. Perhaps you missed providers: [PopupService]?'
			);
		}
		this._anchor = anchor;
	}

	//forward a few properties from core
	public get openedPopup$() {
		return this.popupCore.openedPopup$;
	}
	public get isOpened$() {
		return this.popupCore.isOpened$;
	}

	public create(component: Type<any>, wrap?: boolean) {
		return this.popupCore.create(this._anchor, component, wrap);
	}

	/**
	 * A simple utility for displaying a popup and handling the PopupOutput - which represents the user's
	 * selection - in the provided 'onUpdate' parameter/callback.
	 * Example:
	 *
	 * 		const popupText: SimplePopupInputs = {
	 * 			headingText:      'Are you sure?',
	 * 			descriptionText:  'Are you sure you want to delete these tickets?',
	 * 			customSaveText:   'Yes I'm sure',
	 * 			customCancelText: 'No never mind'
	 * 		};
	 * 		this.popupService.openAndHandleUpdate(popupText, (output) => {
	 * 			if (output.type === PopupOutputType.update) {
	 * 				this.deleteTickets();
	 * 			}
	 * 		});
	 *
	 * @param inputData For instance, a SimplePopupInputs, specifying the strings for the heading, description, save and cancel buttons
	 * @param onUpdate The function that is given the PopupOutput representing the user's choice/answer
	 * @param component The popup component class to use; defaults to SimplePopupComponent
	 */
	public openAndHandleUpdate(
		inputData: any,
		onUpdate: (output: PopupOutput) => void,
		component: Type<any> = SimplePopupComponent,
		blockBackground = true // Default because graying the background looks better if nothing else
	) {
		const popup = this.create(component, blockBackground);
		popup.output$.pipe(last()).subscribe((out) => {
			onUpdate(out);
		});
		// Can this leak?
		// Kurtis: "technically it can leak if the component containing that code is destroyed before that emits.
		// Practically, no, it's not going to leak"
		popup.open(inputData);
	}

	/**
	 * @deprecated
	 */
	public openMultiPopup(popups: Popup[], openData: any[]) {
		return this.popupCore.openMultiPopup(popups, openData);
	}
}
